import { useEffect, useMemo, useRef, useState } from 'react'
import { IconLink } from '../../components/icons'
import { Avatar } from '../../components/ui/Avatar'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { CheckGlyph, SearchGlyph } from '../../components/ui/lucide-glyphs'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { SHARE_PEOPLE, filterSharePeople } from './share-people'

/** Matches `RfqShareQuoteModal.tsx:96` — the sibling share surface in this
 * vertical already reverts its copied label after 2s rather than holding it for
 * the dialog's life, and there is no reason for the two to differ. */
const COPIED_RESET_MS = 2000

export interface RfqShareModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** Writes the internal `/rfq/{draftRfqId}` deep link to the clipboard and
   * raises the success/failure toast. Owned by `RfqReviewPage` (which holds the
   * draft id) rather than here, so this component stays presentational and the
   * ADR-0082 §6 copy-link behavior lives in exactly one place — the page.
   *
   * RESOLVES `true` ONLY IF THE CLIPBOARD WRITE ACTUALLY SUCCEEDED. Gate-5 C2:
   * this used to be `() => void`, so the page's promise was discarded and the
   * button relabelled to "Link copied" synchronously — before the write had even
   * been attempted. On a denied clipboard permission the label then asserted a
   * success that never happened, and held it. The boolean is what makes the
   * label honest; do not widen this back to `void`. */
  readonly onCopyLink: () => Promise<boolean>
  /** DISPLAY-ONLY, and deliberately so — see the component doc comment. Called
   * with the selected `SharePerson.id`s when the primary action is used with a
   * non-empty selection; never called for a bare "Done". */
  readonly onShareWith: (personIds: readonly string[]) => void
}

/**
 * UID-466 / ADR-0152 — the compose-page ("Build your RFQ request", `/rfq/new`)
 * share modal, matching the design at `Fictiv Platform.dc.html:4824-4854`. The
 * Request-summary rail's Share button opens this instead of copying silently;
 * `dc.html:9776` wires that button to `openShare('quote','this RFQ')`.
 *
 * TWO THINGS HERE ARE NOT REAL, AND BOTH ARE RECORDED IN ADR-0152:
 *
 * 1. **The roster is a hardcoded stub** (`share-people.ts`) — there is no tenant
 *    people-directory endpoint to fetch. See that module's doc comment.
 * 2. **Selecting people grants no access.** It calls `onShareWith` and closes; no
 *    share ACL is created, because none exists to create — `SharedSession` is a
 *    single opaque link token with one `openedByUserId`, not a per-recipient
 *    grant. The design prototype's own `shareInvite()` (`dc.html:7354`) likewise
 *    only flashes a message.
 *
 * Gate-5 C1 stated those two facts IN THE DIALOG via `.rfq-share-notice`;
 * UID-485 a1 REMOVED that notice for design parity, so they now survive only in
 * the primary action's copy, this comment, and the ADR. The
 * earlier build showed named colleagues with job titles, flipped their rows to a
 * teal ✓ "Added", and raised a green "Shared with 2 people." success toast — a
 * buyer could reasonably have believed a teammate had been given the RFQ and
 * waited for a review that would never come. The COPY LINK action is the only
 * real capability in this dialog: it yields the tenant-scoped `/rfq/{id}` deep
 * link ADR-0082 §6 established, gated `account:read` and 404-ing cross-tenant.
 *
 * DELIBERATE COPY DIVERGENCES FROM THE COMP (Gate-5 C3, W11) — the `.dc.html` is
 * authoritative on design, so each is called out rather than passed off as
 * fidelity:
 *   - Subtitle: the comp hardcodes "Share this **part** with a teammate, or copy
 *     a link." (`dc.html:8808`) for every `kind`, including this RFQ one. That is
 *     a comp bug; "RFQ" is app-owned copy.
 *   - The comp's primary action composes "Add 2 **persons**" (`dc.html:7354`);
 *     this uses "people".
 *   - The notice line and the honest primary-action copy have no comp counterpart
 *     at all — they exist because the comp has no non-functional state to signal.
 *
 * Everything ELSE is real: the filter implements the design's own name-or-role
 * predicate, the selection is genuine state, and the whole dialog is
 * keyboard-operable through the shared `Modal` primitive (portal, focus trap,
 * Escape, backdrop dismiss).
 */
export function RfqShareModal({ open, onClose, onCopyLink, onShareWith }: RfqShareModalProps) {
  const [query, setQuery] = useState('')
  // UID-610 — `useRangeSelection` additionally carries the Gmail/Finder
  // shift-click anchor. Order was never meaningful here (`onShareWith` only
  // ever reads `.length`), so the hook's `Set` is a drop-in for the old
  // ordered array.
  const addedRangeSelection = useRangeSelection()
  const { selectedIds: addedIds } = addedRangeSelection
  const [copied, setCopied] = useState(false)
  const searchRef = useRef<HTMLInputElement | null>(null)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Gate-5 W4 — reset on the CLOSING transition, not the opening one. Resetting
  // on open ran a frame late (a passive effect commits after the render it
  // follows), so a reopened dialog painted once with the previous selection and
  // a stale "Link copied" before blanking. jsdom flushes effects inside act(),
  // so no test could have seen that; `RfqReviewPage` additionally remounts this
  // component on open, which makes the leak unreachable from the page too.
  // biome-ignore lint/correctness/useExhaustiveDependencies: addedRangeSelection.clear closes over the same stable setState every render, so omitting it doesn't risk a stale write.
  useEffect(() => {
    if (open) return
    setQuery('')
    addedRangeSelection.clear()
    setCopied(false)
  }, [open])

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    },
    [],
  )

  const visiblePeople = useMemo(() => filterSharePeople(SHARE_PEOPLE, query), [query])

  // Selection is keyed by id at THIS level, never inside a row: a filtered-out
  // row is unmounted, and row-local state would silently drop the selection the
  // moment the user typed in the search box.
  //
  // UID-610 — shift-click range selection over the currently VISIBLE
  // (search-filtered) people list (Gmail/Finder semantics, same as
  // UID-470's Parts Library). `shiftKey` reaches here from the row's
  // `onClick`, not the checkbox's `onChange` (a `ChangeEvent` carries no
  // modifier-key state).
  function togglePerson(id: string, shiftKey: boolean) {
    addedRangeSelection.toggle(
      id,
      visiblePeople.map((person) => person.id),
      shiftKey,
    )
  }

  async function handleCopyLink() {
    const succeeded = await onCopyLink()
    if (!succeeded) return // Gate-5 C2 — never claim a copy that did not happen.

    setCopied(true)
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
  }

  function handlePrimaryAction() {
    if (addedIds.size > 0) onShareWith([...addedIds])
    onClose()
  }

  const addedCount = addedIds.size
  // Gate-5 C1 — the primary action must not promise a grant it cannot make. It
  // names what actually happens (a note, for the buyer's own reference) instead
  // of the comp's "Add N person(s)".
  const primaryLabel = addedCount === 0 ? 'Done' : `Note ${addedCount} for reference`

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Share · this RFQ"
      subtitle="Share this RFQ with a teammate, or copy a link."
      maxWidth={480}
      className="rfq-share-modal"
      initialFocusRef={searchRef}
      footer={
        <div className="rfq-share-footer">
          <Button variant="outline" onClick={() => void handleCopyLink()}>
            <IconLink />
            {copied ? 'Link copied' : 'Copy link'}
          </Button>
          <Button variant="primary" onClick={handlePrimaryAction}>
            {primaryLabel}
          </Button>
        </div>
      }
    >
      {/* UID-485 a1 — the Gate-5 C1/W2 placeholder-roster notice that used to sit
       * here was removed for design parity (no comp counterpart). The honest
       * framing it carried is NOT restated anywhere in this dialog; if that
       * disclosure is wanted back, re-add it here rather than as a toast — a
       * toast raised outside an `aria-modal` subtree is announced unreliably. */}
      <search className="rfq-share-search">
        <SearchGlyph aria-hidden="true" className="rfq-share-search-icon" />
        <input
          ref={searchRef}
          type="search"
          className="rfq-share-search-input"
          // The design has no visible label above this field, only the
          // placeholder — which is not an accessible name (WCAG 3.3.2), so the
          // name is supplied here instead of inventing a label the comp lacks.
          aria-label="Search people by name or role"
          placeholder="Search people by name or role…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </search>

      {/* Gate-5 W1 (WCAG 4.1.3 Status Messages) — filtering changes the row count
       * with no other announcement, and axe cannot detect a MISSING live region,
       * so the green a11y test did not cover this. A screen-reader user typing
       * "quality" otherwise hears nothing and cannot tell one match from none.
       *
       * ONE node per state, not two: the no-results message IS the live region,
       * and the count is announced only when there are rows. A visually-hidden
       * status carrying the same string as the visible paragraph would announce
       * "No people match that search." twice. */}
      {visiblePeople.length === 0 ? (
        <output className="rfq-share-empty">No people match that search.</output>
      ) : (
        <>
          <output className="sr-only">
            {`${visiblePeople.length} ${visiblePeople.length === 1 ? 'person' : 'people'} match.`}
          </output>
          <ul className="rfq-share-list">
            {visiblePeople.map((person) => {
              const added = addedIds.has(person.id)
              return (
                <li key={person.id} className="rfq-share-row">
                  {/* Gate-5 W3 — a real multi-select, so a real checkbox. This was an
                   * `aria-pressed` toggle whose accessible name ALSO changed with
                   * its state ("Add X" / "Added X"), which double-encodes the state
                   * and reads as a double negative ("Add X, not pressed"); the ARIA
                   * APG is explicit that a toggle's label should not move with its
                   * state. Satisfying both that and WCAG 2.5.3 Label in Name is
                   * impossible with a state-labelled button — the signal that the
                   * control was mis-typed. As a checkbox the accessible name is the
                   * person (the row's own visible text, so 2.5.3 is satisfied
                   * trivially) and the state lives in `checked`. The comp's pill is
                   * kept as the visual affordance, `aria-hidden` so it does not
                   * re-announce the state it depicts. */}
                  <label className="rfq-share-row-label">
                    <input
                      type="checkbox"
                      className="rfq-share-checkbox sr-only"
                      checked={added}
                      // UID-610 — reads `event.shiftKey` off `onClick`; a
                      // real click anywhere on the visible label forwards
                      // to this input's own `click` (native label
                      // behavior), carrying the same modifier state.
                      // `onChange` stays a no-op (see `togglePerson`'s doc
                      // comment).
                      onClick={(event) => togglePerson(person.id, event.shiftKey)}
                      onChange={() => {}}
                    />
                    <span className="rfq-share-avatar" aria-hidden="true">
                      <Avatar name={person.name} size={34} />
                    </span>
                    <span className="rfq-share-person">
                      <span className="rfq-share-person-name" title={person.name}>
                        {person.name}
                      </span>
                      <span className="rfq-share-person-role" title={person.role}>
                        {person.role}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={
                        added
                          ? 'rfq-share-add-pill rfq-share-add-pill--added'
                          : 'rfq-share-add-pill'
                      }
                    >
                      {added ? (
                        <>
                          <CheckGlyph aria-hidden="true" className="rfq-share-add-check" />
                          Added
                        </>
                      ) : (
                        '+ Add'
                      )}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </Modal>
  )
}
